import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ConfirmDialogComponent,
  DEFAULT_SNACKBAR_CONFIG,
  Logger,
  OrganizationService,
} from 'src/app/@shared';
import * as dayjs from 'dayjs';
import { combineLatest, map } from 'rxjs';
import { MatSidenav } from '@angular/material/sidenav';
import {
  Division,
  DivisionService,
  Event,
  EventService,
  EventType,
  EventTypeService,
} from '../..';
import { Filter } from 'src/app/@shared/models/filter.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ListViewStateService } from '../../services/list-view-state.service';
import { AuthorizationService } from 'src/app/@shared/services/authorization.service';

const log = new Logger('EventsComponent');

@Component({
  selector: 'app-events',
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss'],
})
export class EventsComponent<
  TEvent extends Event,
  TDivision extends Division,
  TEventType extends EventType
> implements OnInit, OnDestroy {
  @ViewChild(MatSidenav) sidenav!: MatSidenav;
  @ViewChild('filterSidenav', { read: ElementRef }) filterSidenavElement?: ElementRef<HTMLElement>;
  @ViewChild('filterToggleButton', { read: ElementRef }) filterToggleButton?: ElementRef<HTMLElement>;
  @ViewChild('eventsTable', { static: false }) eventsTable: any;
  @ViewChild('eventsExpansion', { static: false }) eventsExpansion: any;
  maxDate: Date = new Date(9999, 0, 1);
  viewModel$ = combineLatest([
    this.eventService.viewMode$,
    this.eventService.filters$,
    this.divisionService.divisionsList$,
    this.eventTypeService.eventTypesList$,
  ]).pipe(
    map(([viewMode, filters, divisions, eventTypes]) => ({
      viewMode,
      filters,
      divisions,
      eventTypes,
    }))
  );

  filtersForm = new FormGroup({
    divisions: new FormControl<Division[]>([], { nonNullable: true }),
    endDate: new FormControl<string | null>(null),
    eventTypeId: new FormControl<string | null>(null),
    includeExpiredEvents: new FormControl<boolean>(false, { nonNullable: true }),
    search: new FormControl<string | null>(null),
    startDate: new FormControl<string | null>(null),
  });

  constructor(
    private eventService: EventService<TEvent>,
    private divisionService: DivisionService<TDivision>,
    private eventTypeService: EventTypeService<TEventType>,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar,
    private orgService: OrganizationService,
    private listViewState: ListViewStateService,
    private authorizationService: AuthorizationService
  ) { }

  ngOnInit(): void {
    log.debug('init');
    this.clearSearch();
    this.eventService.clearStatusCache();
    this.syncExpiredVisibilityToEventService();
    this.filtersForm.patchValue({
      includeExpiredEvents: this.listViewState.listViewStateValue.includeExpiredEvents ?? false,
    }, { emitEvent: false });
    this.eventService.reload();

    this.viewModel$.subscribe(({ filters, divisions, eventTypes }) => {
      const divisionFilters = filters
        .filter(f => f.fieldName === 'divisionId')
        .map(f => divisions.find(d => d.Id === f.value))
        .filter((d): d is TDivision => !!d);

      const eventTypeFilter = filters.find(f => f.fieldName === 'eventTypeId');
      const startDateFilter = filters.find(f => f.fieldName === 'startDate');
      const endDateFilter = filters.find(f => f.fieldName === 'endDate');
      const patch: any = {};

      if (startDateFilter?.value !== undefined)
        patch.startDate = startDateFilter.value ? new Date(startDateFilter.value) : null;

      if (endDateFilter?.value !== undefined)
        patch.endDate = endDateFilter.value ? new Date(endDateFilter.value) : null;

      if (divisionFilters !== undefined)
        patch.divisions = divisionFilters;

      if (eventTypeFilter !== undefined)
        patch.eventTypeId = eventTypeFilter
          ? eventTypes.find(et => et.Id === eventTypeFilter.value) || null
          : null;

      this.filtersForm.patchValue(patch, { emitEvent: false });

    });
  }

  onSearch(event: any) {
    this.eventService.search(event.target.value);
  }

  onDateRangePickerOpen() {
    this.filtersForm.controls.startDate.reset();
    this.filtersForm.controls.endDate.reset();
  }

  onDateRangeChange() {
    let startDate = dayjs(this.filtersForm.controls.startDate.value).format();
    let endDate = dayjs(this.filtersForm.controls.endDate.value).format();
    let isbefore = dayjs(startDate).isBefore(endDate);
    let isSame = dayjs(startDate).isSame(endDate);

    if (startDate === 'Invalid Date' && endDate === 'Invalid Date') {
      this.eventService.dateRange('', '');
      return;
    }

    if (startDate === 'Invalid Date') {
      this.eventService.dateRange('', endDate);
    } else if (endDate === 'Invalid Date') {
      this.eventService.dateRange(startDate, '');
    } else if (this.filtersForm.valid) {
      this.eventService.dateRange(startDate, endDate);
    }
  }

  onFilterChange(event: any, controlName?: string) {
    const name = controlName ?? event.source?.ngControl?.name;
    if (!name) return;
    switch (name) {
      case 'divisions':
        // get the selected division ids
        const values: TDivision[] = event.value;
        // remove any existing division id filters
        this.eventService.removeFilterByFieldName('divisionId');
        // init the array of division filters
        const divisionFilters: Filter[] = [];
        // loop through the selected division ids and add each as a filter
        values.forEach((value) => {
          // init the new filter
          const divisionFilter: Filter = {
            displayText: value.DivisionName,
            fieldName: 'divisionId',
            value: value.Id,
          };
          // add the filter to the list of filters
          divisionFilters.push(divisionFilter);
        });
        // add the filters to the event service
        this.eventService.addFilters(divisionFilters);
        break;
      case 'eventTypeId':
        //remove any existing event type filter
        this.eventService.removeFilterByFieldName('eventTypeId');

        if (event.value !== '') {
          // init the new filter
          const eventTypeFilter: Filter = {
            displayText: event.value.EventTypeName,
            fieldName: 'eventTypeId',
            value: event.value.Id,
          };
          // add the filter to the list of filters
          this.eventService.addFilters([eventTypeFilter]);
        }
        break;
      case 'startDate':
        if (this.filtersForm.controls.startDate.value === null) {
          this.eventService.removeFilterByFieldName('startDate');
          break;
        }
        let startDate = dayjs(this.filtersForm.controls.startDate.value).format();
        this.eventService.removeFilterByFieldName('startDate');
        const startDateFilter: Filter = {
          displayText: `Start Date ${dayjs(startDate).format('MM/DD/YYYY')}`,
          fieldName: 'startDate',
          value: startDate
        }
        this.eventService.addFilters([startDateFilter]);
        break;
      case 'endDate':
        if (this.filtersForm.controls.endDate.value === null) {
          this.eventService.removeFilterByFieldName('endDate');
          break;
        }
        let endDate = dayjs(this.filtersForm.controls.endDate.value).format();
        this.eventService.removeFilterByFieldName('endDate');
        const endDateFilter: Filter = {
          displayText: `End Date ${dayjs(endDate).format('MM/DD/YYYY')}`,
          fieldName: 'endDate',
          value: endDate
        }
        this.eventService.addFilters([endDateFilter]);
        break;
    }
  }

  toggleView(mode: string) {
    this.eventService.toggleViewMode(mode);
  }

  onShowExpiredEventsChange(event: { checked: boolean }): void {
    const includeExpired = event.checked;
    this.listViewState.updateListViewState({ includeExpiredEvents: includeExpired });
    this.eventService.setShowExpiredEvents(!includeExpired);
  }

  private syncExpiredVisibilityToEventService(): void {
    const includeExpired = this.listViewState.listViewStateValue.includeExpiredEvents ?? false;
    this.eventService.syncShowExpiredEventsFromUiState(!includeExpired);
  }

  private resetExpiredEventsFilter(): void {
    this.filtersForm.controls.includeExpiredEvents.setValue(false);
    this.listViewState.updateListViewState({ includeExpiredEvents: false });
    this.eventService.setShowExpiredEvents(true);
  }

  clearSearch() {
    this.filtersForm.controls.search.setValue('');
    this.eventService.search('');
    if (this.eventsTable) {
      this.eventsTable.clearSelection();
    } else if (this.eventsExpansion) {
      this.eventsExpansion.clearSelection();
    }
  }

  removeFilter(filter: Filter) {
    switch (filter.fieldName) {
      case 'divisionId':
        this.filtersForm.patchValue({
          divisions: this.filtersForm.controls.divisions.value.filter(
            (item: Division) => item.Id !== filter.value
          ),
        });
        break;

      case 'eventTypeId':
        this.filtersForm.patchValue({ eventTypeId: '' });
        break;

      case 'startDate':
        this.filtersForm.patchValue({ startDate: '' });
        break;

      case 'endDate':
        this.filtersForm.patchValue({ endDate: '' });
        break;
    }
    this.eventService.removeFilter(filter);
  }

  resetFilters() {
    this.filtersForm.reset();
    this.resetExpiredEventsFilter();
    this.clearSearch();
    this.eventService.clearFilters();
    this.sidenav.toggle();
  }

  clearFilters() {
    this.filtersForm.reset();
    this.resetExpiredEventsFilter();
    this.eventService.clearFilters();
    this.clearSearch();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.sidenav?.opened) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (this.filterSidenavElement?.nativeElement.contains(target)) return;
    if (this.filterToggleButton?.nativeElement.contains(target)) return;
    if (target.closest('.cdk-overlay-container')) return;

    this.sidenav.close();
  }

  addEvent() {
    this.router.navigate([`0`], { relativeTo: this.route });
  }

  deleteSelectedRecords() {
    let selectedRecords: any;
    if (this.eventsTable) {
      selectedRecords = this.eventsTable.getSelectedSectionRecords();
    } else if (this.eventsExpansion) {
      selectedRecords = this.eventsExpansion.getSelectedSectionRecords();
    }
    if (selectedRecords && selectedRecords.length > 0) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: `Do you want to remove Event(s)?`,
        },
        disableClose: true,
      });

      confirmDialog.afterClosed().subscribe((confirmResult) => {
        if (confirmResult) {
          this.eventService.deleteEvents(selectedRecords).subscribe({
            next: () => {
              this.matSnackBar.open(
                `Event(s) deleted`,
                'OK',
                DEFAULT_SNACKBAR_CONFIG
              );
              this.eventService.reload();
              if (this.eventsTable) {
                this.eventsTable.clearSelection();
              } else if (this.eventsExpansion) {
                this.eventsExpansion.clearSelection();
              }
            },
            error: (error) => {
              log.error('Error in deleting event', error);

              if (error.error.value) {
                throw new Error(error.error.value);
              } else {
                throw new Error(error.message);
              }
            },
          });
        }
      });
    }
  }

  downloadJSON() {
    this.eventService.downloadEventsJSON();
  }

  downloadCSV() {
    this.eventService.downloadEventsCSV();
  }

  ngOnDestroy() {
    this.orgService.showShellBanner(false);
    this.eventService.search('');
    this.eventService.clearFilters();
  }

  exportGalleyProof() {
    let recordId;
    if (this.eventsTable) {
      recordId = this.eventsTable.getSelectedSectionRecords()[0].Detail.Id;
    } else if (this.eventsExpansion) {
      recordId = this.eventsExpansion.getSelectedSectionRecords()[0].Detail.Id;
    }
    this.eventService.exportAllGalleyProof(recordId).subscribe(res => {

      const contentDisposition = res.headers.get('content-disposition');
      let fileName = 'galleyProof.pdf';

      if (contentDisposition) {
        const utf8FileNameMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/);
        if (utf8FileNameMatch && utf8FileNameMatch[1]) {
          fileName = decodeURIComponent(utf8FileNameMatch[1]);
        } else {
          const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1];
          }
        }
      }

      const blob = new Blob([res.body!], { type: res.body!.type });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    })
  }

  isAdmin(): boolean {
    return this.authorizationService.checkAccess('admin||eventmang');
  }
}
